import { DataGrid } from "devextreme-react";
import { Column, DataGridRef } from "devextreme-react/data-grid";
import SarDetails from "./SarDetails";
import {
  calculateExpiredOptions,
  calculateNonVestedGain,
  calculateNonVestedGainForSymbol,
  calculateOptionsToBeVested,
  calculateOptionsVested,
  calculateTaxableAmount,
  calculateTotalAmount,
  calculateTotalAmountForSymbol,
  calculateTotalExercisedForItem,
  calculateTotalTaxableAmountForSymbol,
  calculateVestedGain,
  calculateVestedGainForSymbol,
} from "./sar-utils";
import { formatDate, formatNumber } from "devextreme/localization";
import type { Tranch, FAWithPrice } from "@/types/types";
import { useRef, useState } from "react";
import { openPopup } from "@helpers/DxHelper";
import { createPortal } from "react-dom";
import { useLTIPTranch } from "@hooks/useLTIPTranch";
import { GridSkeleton } from "@/components/Skeletons";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import TickerTotal from "./SarTickerTotal";
import { onRowPreparedNeedToDiscussWithClient } from "../RSU/rsu-psu-utils";
import { PopupRef } from "devextreme-react/cjs/popup";

export function customHeader(id: string, firstRow: string, second?: string) {
  return (
    <div
      id={id}
      className={`group relative mt-auto flex w-full flex-col items-center justify-center p-4 text-sm leading-tight print:h-fit`}
    >
      <span className={`text-center uppercase`}>{firstRow}</span>
      {second ? <span className={`text-center uppercase`}>{second}</span> : null}
    </div>
  );
}

export function cellRenderer<T extends React.ReactNode>(
  cellData: any,
  id: string,
  value: T,
  className = ""
) {
  return (
    <div
      id={`${id}-${cellData?.rowIndex}`}
      className={`relative text-sm screen:py-[0.8438rem] print:px-2 print:py-2 print:text-xs ${className}`}
    >
      {value ? value : ""}
    </div>
  );
}

type Props = { data: FAWithPrice };

function Grid({ data }: Props) {
  const { data: sarItems, isSuccess, isLoading, isError } = useLTIPTranch(data?.externalCode);
  const popupRef = useRef<PopupRef>(null);
  const [selectedData, setSelectedData] = useState<Tranch>();
  const dxGridRef = useRef<DataGridRef>(null);

  const stockCombinedElement = document.getElementById("stock-combined-values");

  const onContentReady = () => {
    const gridInstance = dxGridRef?.current?.instance;
    if (!gridInstance) return;

    const element = gridInstance().element().querySelector(".dx-sort") as HTMLElement;

    if (element) {
      element.style.marginTop = "60%";
    }
  };

  const tickerCombinedData = [
    {
      header: "Amount",
      value: formatNumber(calculateTotalAmountForSymbol(data, sarItems?.data ?? []), "currency"),
    },
    {
      header: "Vested Gain",
      value: formatNumber(calculateVestedGainForSymbol(data, sarItems?.data ?? []), "currency"),
    },
    {
      header: "Non-Vested Gain",
      value: formatNumber(calculateNonVestedGainForSymbol(data, sarItems?.data ?? []), "currency"),
    },
    {
      header: "Taxable Amount",
      value: formatNumber(
        calculateTotalTaxableAmountForSymbol(data, sarItems?.data ?? []),
        "currency"
      ),
    },
  ];

  return (
    <>
      <div className="py-3" />
      {createPortal(
        <TickerTotal tickerCombinedData={tickerCombinedData} ticker={data?.ticker} />,
        stockCombinedElement!
      )}
      {isLoading ? <GridSkeleton columns={15} rows={4} /> : null}
      {isError ? <div>Failed fetching tranches for selected ticker</div> : null}
      {isSuccess ? (
        <>
          <DataGrid
            id={`sars-grid`}
            className="ao-grid"
            dataSource={sarItems?.data}
            showBorders={true}
            showRowLines={true}
            ref={dxGridRef}
            showColumnLines={true}
            keyExpr={data.externalCode}
            width={"100%"}
            onContentReady={onContentReady}
            wordWrapEnabled={true}
            onRowPrepared={onRowPreparedNeedToDiscussWithClient}
            onCellPrepared={(e) => {
              const style = e.cellElement.style;
              if (e.rowType === "header") {
                style.backgroundColor = "var(--primary-color)";
                style.color = "white";
              }
            }}
          >
            <Column
              showInColumnChooser={false}
              width={48}
              cellRender={(cellData: ColumnCellTemplateData<Tranch>) => (
                <button
                  className="flex h-full w-full items-center justify-center"
                  onClick={() => {
                    setSelectedData(cellData.row.data);
                    openPopup(popupRef);
                  }}
                >
                  <img src="/icons/eye-icon.svg" alt="" />
                </button>
              )}
            />
            <Column
              dataField="options"
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-options`, "Options", "Granted")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return cellData?.data?.options
                  ? cellRenderer(
                      cellData,
                      "options-granted",
                      formatNumber(cellData?.data?.options, "#,##0")
                    )
                  : "";
              }}
            />
            <Column
              dataField="grantDate"
              caption="Date of Grant"
              dataType="date"
              allowSorting={false}
              sortOrder={"asc"}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-options`, "Date of", "Grant")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return cellData?.data?.grantDate
                  ? cellRenderer(
                      cellData,
                      "date-of-grant",
                      formatDate(new Date(cellData?.data?.grantDate), "MMM dd, yyyy")
                    )
                  : "";
              }}
            />
            <Column
              dataField="optionsVested"
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-options-vested`, "Options", "vested")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateOptionsVested(cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "options-vested",
                      formatNumber(calculateOptionsVested(cellData?.data), "#,##0")
                    )
                  : "";
              }}
            />
            <Column
              dataField="optionsToBeVested"
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-options-to-be-vested`, "Options to", "be vested")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateOptionsToBeVested(cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "options-to-be-vested",
                      formatNumber(calculateOptionsToBeVested(cellData?.data), "#,##0")
                    )
                  : "";
              }}
            />
            <Column
              dataField="expiredOptions"
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-options-expired`, "Expired", "Options")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateExpiredOptions(cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "expired-options",
                      formatNumber(calculateExpiredOptions(cellData?.data), "#")
                    )
                  : "";
              }}
            />
            <Column
              dataField="vestingPercentage"
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              format="percent"
              headerCellRender={() =>
                customHeader(`${data.externalCode}-vesting-percentage`, "Vesting", "Percentage")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return cellData?.data?.vestingPercentage
                  ? cellRenderer(
                      cellData,
                      "vesting-percentage",
                      formatNumber(cellData?.data?.vestingPercentage / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="vestingDate"
              dataType={"date"}
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-vesting-schedule`, "Vesting", "Schedule")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return cellData?.data?.vestingDate
                  ? cellRenderer(
                      cellData,
                      "vesting-schedule",
                      formatDate(new Date(cellData?.data?.vestingDate), "MMM dd, yyyy")
                    )
                  : "";
              }}
            />
            <Column
              dataField="dateOfExpiration"
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              dataType={"date"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-date-of-expiration`, "Expiration", "Date")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return cellData?.data?.dateOfExpiration
                  ? cellRenderer(
                      cellData,
                      "date-of-grant",
                      formatDate(new Date(cellData?.data?.dateOfExpiration), "MMM dd, yyyy")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"costOrGrant"}
              allowSorting={false}
              allowEditing={false}
              width={"fit"}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-grant-price`, "Grant", "Price")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return cellData?.data?.costOrGrant
                  ? cellRenderer(
                      cellData,
                      "grant-price",
                      formatNumber(cellData?.data?.costOrGrant, "$#,##0.00")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"totalAmount"}
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-total-amount`, "Total", "Amount")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateTotalAmount(data, cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "total-amount",
                      formatNumber(calculateTotalAmount(data, cellData?.data), "currency")
                        .replaceAll("(", "")
                        .replaceAll(")", "")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"vestedGain"}
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-vested-gain`, "Vested", "Gain")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateVestedGain(data, cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "vested-gain",
                      formatNumber(calculateVestedGain(data, cellData?.data), "currency")
                        .replaceAll("(", "")
                        .replaceAll(")", "")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"nonVestedGain"}
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-non-vested-gain`, "Non-vested", "Gain")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateNonVestedGain(data, cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "non-vested-gain",
                      formatNumber(calculateNonVestedGain(data, cellData?.data), "currency")
                        .replaceAll("(", "")
                        .replaceAll(")", "")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"taxableAmount"}
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-taxable amount`, "Taxable", "Amount")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateTaxableAmount(data, cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "non-vested-gain",
                      formatNumber(calculateTaxableAmount(data, cellData?.data), "currency")
                        .replaceAll("(", "")
                        .replaceAll(")", "")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"totalActualExercised"}
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(
                  `${data.externalCode}-total-actual-exercised`,
                  "Total Actual",
                  "Exercised"
                )
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateTotalExercisedForItem(cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "non-vested-gain",
                      formatNumber(calculateTotalExercisedForItem(cellData?.data) || 0, "#0")
                    )
                  : "";
              }}
            />
            <Column
              dataField={"totalRemaining"}
              allowSorting={false}
              allowEditing={false}
              alignment={"center"}
              width={"fit"}
              headerCellRender={() =>
                customHeader(`${data.externalCode}-total-remaining`, "Total", "Remaining")
              }
              cellRender={(cellData: ColumnCellTemplateData<Tranch, string>) => {
                return calculateOptionsVested(cellData?.data)
                  ? cellRenderer(
                      cellData,
                      "total-remaining",
                      formatNumber(calculateOptionsVested(cellData?.data), "#,##0")
                    )
                  : "";
              }}
            />
          </DataGrid>
          <SarDetails tranchCode={data.externalCode} data={selectedData} popupRef={popupRef} />
        </>
      ) : null}
    </>
  );
}

export default Grid;
